import { useEffect, useRef, useState, useCallback } from "react";
import { createPortal } from "react-dom";
import { MoreHorizontal } from "lucide-react";

import { useOnClickOutside, cx, cycleIndex } from "./dropdown.utils";
import styles from "./dropdown.module.scss";

const CustomDropdown = ({ title = "Acciones", items = [], align = "right", triggerIcon: TriggerIcon }) => {
	const Icon = TriggerIcon || MoreHorizontal;

	const [open, setOpen] = useState(false);
	const [focusIndex, setFocusIndex] = useState(-1);
	const [menuPosition, setMenuPosition] = useState({ top: 0, left: 0, width: 0, openUpwards: false });

	const rootRef = useRef(null);
	const btnRef = useRef(null);
	const listRef = useRef(null);
	const portalRef = useRef(null);

	const close = useCallback(() => {
		setOpen(false);
		setFocusIndex(-1);
		requestAnimationFrame(() => {
			if (btnRef.current) btnRef.current.focus();
		});
	}, []);

	useOnClickOutside(rootRef, (e) => {
		if (portalRef.current && portalRef.current.contains(e.target)) return;
		if (open) close();
	});

	const updatePosition = useCallback(() => {
		if (btnRef.current && portalRef.current) {
			const rect = btnRef.current.getBoundingClientRect();
			const menuHeight = portalRef.current.offsetHeight || 300;
			const viewportHeight = window.innerHeight;
			const spaceBelow = viewportHeight - rect.bottom;
			const spaceAbove = rect.top;

			const shouldOpenUpwards = spaceBelow < menuHeight && spaceAbove > spaceBelow;

			setMenuPosition({
				top: shouldOpenUpwards ? rect.top + window.scrollY - menuHeight - 8 : rect.bottom + window.scrollY + 8,
				left: align === "right" ? rect.right + window.scrollX : rect.left + window.scrollX,
				width: rect.width,
				openUpwards: shouldOpenUpwards,
			});
		} else if (btnRef.current) {
			const rect = btnRef.current.getBoundingClientRect();
			setMenuPosition({
				top: rect.bottom + window.scrollY + 8,
				left: align === "right" ? rect.right + window.scrollX : rect.left + window.scrollX,
				width: rect.width,
				openUpwards: false,
			});
		}
	}, [align]);

	useEffect(() => {
		if (open) {
			updatePosition();
			const timer = setTimeout(updatePosition, 10);
			window.addEventListener("scroll", updatePosition, true);
			window.addEventListener("resize", updatePosition);
			return () => {
				clearTimeout(timer);
				window.removeEventListener("scroll", updatePosition, true);
				window.removeEventListener("resize", updatePosition);
			};
		}
	}, [open, updatePosition]);

	const onButtonKeyDown = (e) => {
		if (e.key === "ArrowDown" || e.key === "Enter" || e.key === " ") {
			e.preventDefault();
			setOpen(true);
			setFocusIndex(0);
		}
	};

	const onMenuKeyDown = (e) => {
		if (e.key === "Escape") {
			e.preventDefault();
			close();
			return;
		}
		if (e.key === "ArrowDown") {
			e.preventDefault();
			setFocusIndex((i) => cycleIndex(i, +1, items.length));
			return;
		}
		if (e.key === "ArrowUp") {
			e.preventDefault();
			setFocusIndex((i) => cycleIndex(i, -1, items.length));
			return;
		}
		if (e.key === "Home") {
			e.preventDefault();
			setFocusIndex(0);
			return;
		}
		if (e.key === "End") {
			e.preventDefault();
			setFocusIndex(items.length - 1);
			return;
		}
		if (e.key === "Enter" || e.key === " ") {
			e.preventDefault();
			const item = items[focusIndex];
			if (item && !item.disabled && typeof item.action === "function") {
				item.action();
			}
			close();
			return;
		}
		if (e.key === "Tab") {
			close();
		}
	};

	useEffect(() => {
		if (!open) return;
		const el = listRef.current?.querySelector(`[data-index="${focusIndex}"]`);
		if (el) el.focus();
	}, [focusIndex, open]);

	return (
		<div className={styles.dropdown} ref={rootRef} data-align={align}>
			<button
				className={styles.iconButton}
				onClick={() => setOpen((v) => !v)}
				onKeyDown={onButtonKeyDown}
				aria-haspopup="menu"
				aria-expanded={open}
				aria-label="Abrir opciones"
				ref={btnRef}
				type="button"
			>
				<Icon size={20} />
			</button>

			{open &&
				createPortal(
					<div
						ref={portalRef}
						className={cx(styles.menuPortal, menuPosition.openUpwards && styles.openUpwards)}
						role="menu"
						tabIndex={-1}
						onKeyDown={onMenuKeyDown}
						style={{
							top: `${menuPosition.top}px`,
							left: align === "right" ? "auto" : `${menuPosition.left}px`,
							right: align === "right" ? `${window.innerWidth - menuPosition.left}px` : "auto",
						}}
					>
						<div className={styles.header}>
							<span className={styles.headerText}>{title}</span>
						</div>
						<div className={styles.list} ref={listRef}>
							{items.map((item, idx) => {
								if (item.separator) {
									return <div key={`separator-${idx}`} className={styles.separator} />;
								}

								const Icon = item.icon;
								return (
									<button
										key={`${item.label}-${idx}`}
										className={cx(styles.item, item.danger && styles.danger, item.disabled && styles.disabled)}
										role="menuitem"
										tabIndex={idx === focusIndex ? 0 : -1}
										data-index={idx}
										onClick={(e) => {
											e.preventDefault();
											e.stopPropagation();
											if (item.disabled) return;
											if (typeof item.action === "function") item.action();
											close();
										}}
										type="button"
									>
										{Icon ? <Icon size={16} className={styles.icon} /> : null}
										<span className={styles.label}>{item.label}</span>
									</button>
								);
							})}
						</div>
					</div>,
					document.body,
				)}
		</div>
	);
};

export default CustomDropdown;
